import {
  addData,
  deleteData,
  readAllData,
  recordingsStoreName,
  setData,
} from '@/database.js';
import { Timestamp } from '@/firebaseConfig.js';
import { Capacitor } from '@capacitor/core';
import uuid from 'uuid-v4';
import { Sentry } from '@/vendors/Sentry';
import {
  checkFileExists,
  deleteSecretFile,
  readAllSecretFiles,
  writeSecretFile,
  audioUrlsFolderName,
} from '@/filesystem';

export const localRecordings = {
  namespaced: true,
  state: getInitialState(),
  actions: {
    async editLocalRecording(
      { state, commit },
      {
        id,
        name,
        status,
        aiTemplateId,
        uploadedChunks,
        recordingId,
        totalChunks,
        integration,
        chunkSize,
      }
    ) {
      const localRecordingIndex = state.localRecordings.findIndex((rec) => rec.id === id);
      if (localRecordingIndex === -1) return;
      const _localRecording = state.localRecordings[localRecordingIndex];
      const recording = {
        ..._localRecording,
        aiTemplateId: aiTemplateId || _localRecording.aiTemplateId,
        name: name || _localRecording.name,
        status: status || _localRecording.status,
        uploadedChunks: uploadedChunks || _localRecording.uploadedChunks,
        recordingId: recordingId || _localRecording.recordingId,
        totalChunks: totalChunks || _localRecording.totalChunks,
        chunkSize: chunkSize || _localRecording.chunkSize,
        integration: integration || _localRecording.integration,
      };

      await setData(recording.id, JSON.parse(JSON.stringify(recording)), recordingsStoreName);

      // Commit the mutation
      commit('editLocalRecording', {
        index: localRecordingIndex,
        recording,
      });
    },
    async resetLocalRecordingUpload({ commit, state, dispatch }, { id }) {
      const recording = state.localRecordings.find((rec) => rec.id === id);
      await dispatch('editLocalRecording', {
        ...recording,
        status: 'local',
        uploadedChunks: undefined,
        recordingId: undefined,
        totalChunks: undefined,
        chunkSize: undefined,
      });
      commit('setProgress', {
        id,
        progress: 0,
      });
    },
    async loadLocalRecordings({ state, dispatch, rootState }) {
      try {
        if (state.loaded) {
          return;
        }

        const localRecordings = await readAllData(recordingsStoreName);

        const allAudioUrlIds = await readAllSecretFiles({ folder: audioUrlsFolderName });

        if (!localRecordings?.length && !allAudioUrlIds?.length) {
          state.loaded = true;
          return;
        }

        state.localRecordings = localRecordings;

        for (const localRecording of localRecordings) {
          if (localRecording.status !== 'local') {
            await dispatch('editLocalRecording', { id: localRecording.id, status: 'local' });
          }
        }

        const audioUrlsIds = allAudioUrlIds.filter(
          (audioUrlId) => localRecordings.findIndex((r) => r.audioUrlId === audioUrlId) === -1
        );
        const { seconds, nanoseconds } = Timestamp.now();
        for (const audioUrlId of audioUrlsIds) {
          const restoredRecording = {
            status: 'local',
            id: uuid(),
            name: `Restored ${audioUrlId}`,
            createdAt: {
              seconds,
              nanoseconds,
            },
            organizationId: rootState.currentOrganizationId,
            audioUrlId,
            mimeType: Capacitor.isNativePlatform() ? 'audio/aac' : 'audio/webm',
            duration: 0,
          };
          state.localRecordings.push(restoredRecording);
          await addData(
            restoredRecording.id,
            JSON.parse(JSON.stringify(restoredRecording)),
            recordingsStoreName
          );
        }
        state.loaded = true;
      } catch (e) {
        Sentry.captureException(e);
        console.error('loadLocalRecordings', e);
      }
    },
    async addLocalRecording({ commit }, recording) {
      const audioUrlId = uuid();

      await writeSecretFile({
        fileName: audioUrlId,
        data: recording.audioURL,
        folder: audioUrlsFolderName,
      });

      // Force javascript to garbage collect the dataUrl
      delete recording.audioURL;
      const newLocalRecording = {
        ...recording,
        audioUrlId,
        status: 'local',
      };
      await addData(
        newLocalRecording.id,
        JSON.parse(JSON.stringify(newLocalRecording)),
        recordingsStoreName
      );
      // Commit the mutation
      commit('addLocalRecording', newLocalRecording);
    },
    async deleteLocalRecording({ commit, state }, recordingId) {
      const localRecordingIndex = state.localRecordings.findIndex((rec) => rec.id === recordingId);
      if (localRecordingIndex === -1) return;

      const { audioUrlId } = state.localRecordings[localRecordingIndex];

      if (await checkFileExists({ folder: audioUrlsFolderName, fileName: audioUrlId })) {
        await deleteSecretFile({
          fileName: audioUrlId,
          folder: audioUrlsFolderName,
        });
      }

      await deleteData(recordingsStoreName, recordingId);
      // Commit the mutation
      commit('deleteLocalRecording', localRecordingIndex);
    },
  },
  mutations: {
    addLocalRecording(state, newLocalRecording) {
      state.localRecordings.unshift(newLocalRecording);
    },
    editLocalRecording(state, { index, recording }) {
      state.localRecordings.splice(index, 1, recording);
    },
    deleteLocalRecording(state, index) {
      state.localRecordings.splice(index, 1);
    },
    setProgress(state, { id, value }) {
      state.progress[id] = value;
    },
    resetState(state) {
      Object.assign(state, getInitialState());
    },
  },
  getters: {
    getLocalRecording: (state) => async (localRecordingId) => {
      const localRecording = state.localRecordings.find(({ id }) => localRecordingId === id);
      return localRecording;
    },
  },
};

function getInitialState() {
  return {
    loaded: false,
    localRecordings: [],
    progress: {},
  };
}
